import { useCallback, useEffect, useState } from 'react';
import type { ScreenProps } from '../../app/routes';
import { useRouter } from '../../app/router';
import { useAuth } from '../../auth/AuthProvider';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import { ApiError, loadDocumentTypes, loadDocuments, loadPeriods, localizedName } from '../documents/api';
import type { DocumentPeriod } from '../documents/filters';
import { EMPTY_JOURNAL_FILTERS } from './entries';
import type { JournalEntry, JournalFilters } from './entries';
import { loadJournal } from './api';
import { JournalView } from './JournalView';

const PAGE_SIZE = 50;

function problemOf(error: unknown): Problem {
  if (error instanceof ApiError) {
    return error.problem;
  }
  return { status: 0, code: 'network', detail: error instanceof Error ? error.message : String(error) };
}

export default function JournalScreen(_props: ScreenProps): JSX.Element {
  const { authorizedFetch } = useAuth();
  const { language } = useI18n();
  const { navigate } = useRouter();

  const [periods, setPeriods] = useState<DocumentPeriod[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Array<{ code: string; name: string }>>([]);
  const [documentLabels, setDocumentLabels] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<JournalFilters>(EMPTY_JOURNAL_FILTERS);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<Problem | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [loadedPeriods, loadedTypes, documents] = await Promise.all([
          loadPeriods(authorizedFetch),
          loadDocumentTypes(authorizedFetch),
          loadDocuments(authorizedFetch, { type: '', status: 'all' }),
        ]);
        if (cancelled) {
          return;
        }
        setPeriods(loadedPeriods);
        setDocumentTypes(
          loadedTypes.map((type) => ({ code: type.code, name: localizedName(type.nameI18n, language) })),
        );
        const labels: Record<string, string> = {};
        for (const document of documents) {
          labels[document.id] = document.number;
        }
        setDocumentLabels(labels);
      } catch (error) {
        if (!cancelled) {
          setProblem(problemOf(error));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, language]);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    try {
      const loaded = await loadJournal(authorizedFetch, filters, page, PAGE_SIZE);
      setEntries(loaded.items);
      setTotal(loaded.total);
    } catch (error) {
      setProblem(problemOf(error));
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, filters, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <JournalView
      periods={periods}
      documentTypes={documentTypes}
      filters={filters}
      entries={entries}
      documentLabels={documentLabels}
      total={total}
      page={page}
      pageSize={PAGE_SIZE}
      problem={problem}
      loading={loading}
      onFiltersChange={(next) => {
        setFilters(next);
        setPage(1);
      }}
      onApply={() => void load()}
      onReset={() => {
        setFilters(EMPTY_JOURNAL_FILTERS);
        setPage(1);
      }}
      onPageChange={setPage}
      onOpenDocument={(id) => navigate(`/documents/${id}`)}
    />
  );
}
